import {
  Card,
  Grid,
  Stack,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import { Box } from '@mui/system';
import { LoadingButton } from '@mui/lab';
import { useAppDispatch, useAppSelector } from '../../../store';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FormikProvider, useFormik } from 'formik';
import { setStatus } from '../../../store/settings/settingsSlice';
import { useEffect, useState } from 'react';
import { decryptString } from '../../../utils/url';
import Loader from '../../../components/Loader';
import { getSettings, patchSettings } from '../../../store/settings/api';
import { LANGUAGE } from '../../../constants';
import DetailsForm from './DetailsForm';
import { ArabicSettingsFAQ, EnglishSettingsFAQ, Language } from '../../../enum';
import faqSchema from '../../../validation/faqSchema';
import { fillFaqState } from '../../../utils/fillFaqData';

const FAQForm = () => {
  const dispatch = useAppDispatch();
  const navigation = useNavigate();
  const params: any = useParams();
  const location = useLocation();
  const state = location.state as any; // Type Casting, then you can get the params passed via router

  const isAddMode = !params?.id;
  const { isLoading } = useAppSelector((state) => state.settings);
  const [response, setResponse] = useState<any>();

  const formik = useFormik({
    initialValues: {
      language: '',
      items: [
        {
          categoryName: '',
          qaList: [
            {
              question: '',
              answer: '',
            },
          ],
        },
      ],
    },
    validationSchema: faqSchema,
    onSubmit: async () => {
      try {
        if (formik?.values?.language == Language.english) {
          const valueData = JSON.stringify(formik?.values?.items);
          const reqBody = {
            id: EnglishSettingsFAQ.id,
            name: EnglishSettingsFAQ.name,
            valueType: EnglishSettingsFAQ.valueType,
            language: formik?.values?.language,
            value: valueData,
            activeState: true,
          };
          await dispatch(patchSettings(reqBody)).unwrap();
        }
        if (formik?.values?.language == Language.arabic) {
          const valueData = JSON.stringify(formik?.values?.items);
          const reqBody = {
            id: ArabicSettingsFAQ.id,
            name: ArabicSettingsFAQ.name,
            valueType: ArabicSettingsFAQ.valueType,
            language: formik?.values?.language,
            value: valueData,
            activeState: true,
          };
          await dispatch(patchSettings(reqBody)).unwrap();
        }
        formik?.resetForm();
        dispatch(setStatus({ status: 'idle' }));
        navigation('/faq');
      } catch (error: any) {}
    },
  });

  useEffect(() => {
    const fetchById = async () => {
      if (isAddMode) return;

      if (state) {
        formik?.setValues(fillFaqState(state));
        setResponse(state);
      } else {
        try {
          const decodeParam = decryptString(params.id);
          const record = await dispatch(
            getSettings({ id: decodeParam })
          ).unwrap();
          formik?.setValues(fillFaqState(record));
          setResponse(record);
        } catch (error: any) {
          navigation('/faq');
        }
      }
    };
    fetchById();
  }, [dispatch, isAddMode, navigation, params?.id, formik?.setValues, state]);

  return (
    <Grid
      container
      spacing={3}
    >
      <Grid
        item
        xs={12}
        md={12}
      >
        {isLoading ? (
          <Loader />
        ) : (
          <FormikProvider value={formik}>
            <form onSubmit={formik?.handleSubmit}>
              <Card sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: 'grid',
                    columnGap: 2,
                    rowGap: 3,
                    gridTemplateColumns: {
                      xs: 'repeat(1, 1fr)',
                      sm: 'repeat(2, 1fr)',
                    },
                  }}
                >
                  <FormControl
                    fullWidth
                    error={Boolean(
                      formik?.touched?.language && formik?.errors?.language
                    )}
                  >
                    <InputLabel>{'Language'}</InputLabel>
                    <Select
                      name='language'
                      labelId='language'
                      id='language'
                      value={formik?.values.language}
                      label={'Language'}
                      onChange={(e) =>
                        formik?.setFieldValue('language', e.target.value)
                      }
                      MenuProps={{
                        style: {
                          maxHeight: 400,
                        },
                      }}
                    >
                      {LANGUAGE?.map((type) => (
                        <MenuItem
                          key={type.value}
                          value={type.value}
                        >
                          {type.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {Boolean(
                      formik?.touched.language && formik?.errors.language
                    ) && (
                      <FormHelperText>
                        {formik?.errors?.language}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Box>

                <DetailsForm
                  isAddMode={isAddMode}
                  response={response}
                  values={formik.values}
                  handleChange={formik.handleChange}
                  setFieldValue={formik.setFieldValue}
                  errors={formik.errors}
                  touched={formik.touched}
                />

                <Stack
                  alignItems='center'
                  sx={{ mt: 3 }}
                  direction='row'
                  justifyContent='flex-end'
                  gap={1}
                >
                  <Button
                    variant='outlined'
                    onClick={() => navigation('/faq')}
                  >
                    {'Cancel'}
                  </Button>

                  <LoadingButton
                    type='submit'
                    variant='contained'
                    loading={formik?.isSubmitting}
                  >
                    {isAddMode ? 'Add FAQ' : 'Edit FAQ'}
                  </LoadingButton>
                </Stack>
              </Card>
            </form>
          </FormikProvider>
        )}
      </Grid>
    </Grid>
  );
};

export default FAQForm;
